<ng-container *ngIf="signupOrganization">
    <div [class.neighborhood-background]="selectedStep !== signupSteps.SELECT_SIGNUP_TYPE">
        <div class="container">
            <form class="auth-form d-flex w-100"
                  [style.padding-bottom.px]="selectedStep !== signupSteps.SELECT_SIGNUP_TYPE ? 50 : 0"
                  [formGroup]="signupForm"
                  (ngSubmit)="createAccount()"
                  novalidate>
                <mat-stepper class="bg-transparent w-100"
                             orientation="vertical"
                             [selectedIndex]="selectedStep"
                             (animationDone)="onStepperAnimationDone()">
                    <!-- step1, select sign-up role: homeowner, board member, property manager -->
                    <mat-step>
                        <ng-container *ngTemplateOutlet="signupContainer; context:{ step: selectSignupTypeStep }"></ng-container>
                    </mat-step>

                    <!-- step2a, for homeowners, provide email address to verify -->
                    <mat-step>
                        <ng-container *ngTemplateOutlet="signupContainer; context:{ step: emailVerificationStep }"></ng-container>
                    </mat-step>

                    <!-- step3a, if email address already exists, send registration email -->
                    <mat-step>
                        <ng-container *ngTemplateOutlet="signupContainer; context:{ step: checkEmailStep }"></ng-container>
                    </mat-step>

                    <!-- step3b, search community (for homeowners) -->
                    <mat-step>
                        <ng-container *ngTemplateOutlet="signupContainer; context:{ step: searchOrganizationsStep }"></ng-container>
                    </mat-step>

                    <!-- step4, collect form fields based on signup roles -->
                    <mat-step>
                        <ng-container *ngTemplateOutlet="signupContainer; context:{ step: signupFormDetails }"></ng-container>
                    </mat-step>

                    <!-- step6, successful sign-up, show confirmation message for homeowner or log in/redirect to launchpad -->
                    <mat-step>
                        <ng-container *ngTemplateOutlet="signupContainer; context:{ step: joinComplete }"></ng-container>
                    </mat-step>
                </mat-stepper>

                <!-- form group and control templates -->
                <ng-template #emailFormControl>
                    <div class="form-group">
                        <label class="text-left font-weight-bold form-control-required" for="email">{{ 'Email' }}</label>
                        <input #emailInput
                               id="email"
                               class="form-control"
                               formControlName="email"
                               placeholder="{{'Your email address'}}"
                               required
                               type="email"
                               [readonly]="isSubmitting"
                               (keyup.enter)="selectedStep === signupSteps.EMAIL_VERIFICATION ? verifyEmail() : null"/>
                        <app-field-error-display [control]="isFieldValid('email')"></app-field-error-display>
                    </div>
                </ng-template>

                <ng-template #groupNameFormControl let-label="label" let-placeholder="placeholder">
                    <div class="form-group">
                        <label class="text-left font-weight-bold form-control-required" for="group-name">{{ label }}</label>
                        <input #groupNameInput
                               id="group-name"
                               class="form-control"
                               formControlName="groupName"
                               autocomplete="organization"
                               placeholder="{{placeholder}}"
                               required
                               type="text"/>
                        <app-field-error-display [control]="isFieldValid('groupName')"></app-field-error-display>
                    </div>
                </ng-template>

                <ng-template #addressFormGroup>
                    <div formGroupName="address">
                        <div class="form-group">
                            <label class="text-left font-weight-bold form-control-required" for="address-search">{{ 'Unit Address' }}</label>
                            <input #addressSearch
                                   id="address-search"
                                   class="form-control"
                                   formControlName="line1"
                                   autocomplete="address-line1"
                                   type="text"
                                   matGoogleMapsAutocomplete
                                   placeholder="{{'Your address'}}"
                                   required
                                   (onAutocompleteSelected)="onAddressAutocompleteSelected($event)"/>
                            <app-field-error-display [control]="isFieldValid('address.line1')"></app-field-error-display>
                        </div>

                        <div class="form-group">
                            <input id="address-line-2"
                                   class="form-control"
                                   formControlName="line2"
                                   autocomplete="address-line2"
                                   placeholder="{{'Apt, suite, bldg, etc. (optional)'}}"
                                   type="text"/>
                        </div>

                        <div class="row px-2">
                            <div class="form-group col-12 col-md-5 px-1">
                                <label class="text-left font-weight-bold form-control-required" for="address-city">{{ 'City' }}</label>
                                <input id="address-city"
                                       class="form-control"
                                       formControlName="city"
                                       autocomplete="address-level2"
                                       placeholder="{{'Your city'}}"
                                       required
                                       type="text"/>
                                <app-field-error-display [control]="isFieldValid('address.city')"></app-field-error-display>
                            </div>

                            <div class="form-group col-8 col-md-4 px-1">
                                <label class="text-left font-weight-bold form-control-required">{{ 'State' }}</label>
                                <lf-geo-region-selector [formGroup]="addressForm"
                                                        [placeholder]="'Your state'"
                                                        [useMatSelect]="true"></lf-geo-region-selector>
                                <app-field-error-display [control]="isFieldValid('address.region')"></app-field-error-display>
                            </div>

                            <div class="form-group col-4 col-md-3 px-1">
                                <label class="text-left font-weight-bold form-control-required" for="address-postal-code">{{ 'Zip' }}</label>
                                <input id="address-postal-code"
                                       class="form-control"
                                       formControlName="postalCode"
                                       autocomplete="postal-code"
                                       required
                                       type="text"
                                       [placeholder]="addressPostalCodeInputMask.placeholder"
                                       [mask]="addressPostalCodeInputMask.mask"/>
                                <app-field-error-display [control]="isFieldValid('address.postalCode')"></app-field-error-display>
                            </div>
                        </div>
                    </div>
                </ng-template>

                <!-- sign-up workflow step templates -->
                <ng-template #selectSignupTypeStep>
                    <div class="grid-layout grid-3">
                        <div class="card animated fadeIn clickable"
                             [attr.tabindex]="0"
                             (keyup.enter)="selectAndProceed(signupFormTypes.HOMEOWNER)"
                             (click)="selectAndProceed(signupFormTypes.HOMEOWNER)">
                            <div class="card-body text-center">
                                <img alt="{{signupFormTypes.HOMEOWNER}}"
                                     src="/assets/images/signup/signup-homeowner.svg"/>
                                <h3>{{ 'Homeowner' }}</h3>
                                <span class="description-text">{{ 'Access my homeowner portal.' }}</span>
                            </div>
                        </div>

                        <div class="card animated fadeIn clickable"
                             [attr.tabindex]="0"
                             (keyup.enter)="selectAndProceed(signupFormTypes.BOARD_MEMBER)"
                             (click)="selectAndProceed(signupFormTypes.BOARD_MEMBER)">
                            <div class="card-body text-center">
                                <img alt="{{signupFormTypes.BOARD_MEMBER}}"
                                     src="/assets/images/signup/signup-boardmember.svg"/>
                                <h3>{{ 'HOA Board Member' }}</h3>
                                <span class="description-text">{{ 'Create a PayHOA account to manage my community.' }}</span>
                            </div>
                        </div>

                        <div class="card animated fadeIn clickable"
                             [attr.tabindex]="0"
                             (keyup.enter)="selectAndProceed(signupFormTypes.PROPERTY_MANAGER)"
                             (click)="selectAndProceed(signupFormTypes.PROPERTY_MANAGER)">
                            <div class="card-body text-center">
                                <img alt="{{signupFormTypes.PROPERTY_MANAGER}}"
                                     src="/assets/images/signup/signup-propertymgr.svg"/>
                                <h3>{{ 'Property Manager' }}</h3>
                                <span class="description-text">{{ 'Create a PayHOA account to manage multiple communities.' }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="font-size-normal text-center pt-4">
                        {{ 'Already have an account? ' }}
                        <app-button type="link"
                                    [buttonClasses]="['mr-0']"
                                    [routeTo]="[loginRoute]">{{ 'Log in' }}
                        </app-button>
                    </div>
                </ng-template>

                <ng-template #emailVerificationStep>
                    <ng-template #emailVerificationPanel>
                        <div class="card">
                            <div class="card-body with-footer">
                                <h2 *ngIf="!brandedSignup">{{ 'Sign up with your email address' }}</h2>
                                <ng-container *ngIf="brandedSignup">
                                    <h2>{{ 'Need to create your account?' }}</h2>
                                    <p class="notification-bar mt-3">{{ 'Once approved by your community, you\'ll receive an email registration link.' }}</p>
                                </ng-container>
                                <ng-container *ngTemplateOutlet="emailFormControl"></ng-container>
                            </div>

                            <div class="card-footer d-flex flex-column justify-content-center">
                                <app-spinner-button *ngIf="isSubmitting"
                                                    [buttonClasses]="['mx-auto']">{{ 'Verifying' }}
                                </app-spinner-button>
                                <app-button *ngIf="!isSubmitting"
                                            type="primary"
                                            [buttonClasses]="['mr-0']"
                                            (onClick)="verifyEmail()">{{ 'Continue' }}
                                </app-button>
                                <ng-container *ngTemplateOutlet="copyrightInfo"></ng-container>
                            </div>
                        </div>
                    </ng-template>

                    <ng-container *ngIf="brandedSignup; else emailVerificationPanel">
                        <div [class.grid-layout]="!isForwarding"
                             [class.grid-3-middle-fixed]="!isForwarding">
                            <app-login-form [header]="'Already have an account? Log in below.'"
                                            [loginForm]="loginForm"></app-login-form>
                            <ng-container *ngIf="!isForwarding">
                                <div class="card card-vertical-separator d-block d-md-none">
                                    <span>{{ 'or' }}</span>
                                </div>
                                <div class="card card-horizontal-separator d-none d-md-flex">
                                    <span>{{ 'or' }}</span>
                                </div>
                                <ng-container *ngTemplateOutlet="emailVerificationPanel"></ng-container>
                            </ng-container>
                        </div>
                    </ng-container>
                </ng-template>

                <ng-template #checkEmailStep>
                    <div class="card">
                        <div class="card-body with-footer">
                            <h2>{{ 'Registration email is on its way' }}</h2>

                            <p class="notification-bar mt-3">
                                {{ 'We have emailed ' + signupForm.get('email').value + ' to complete your account registration.' }}
                            </p>
                        </div>

                        <div class="card-footer d-flex flex-column justify-content-center">
                            <app-button type="primary"
                                        [buttonClasses]="['mr-0']"
                                        [routeTo]="[loginRoute]">{{ 'Back to Login' }}
                            </app-button>
                            <ng-container *ngTemplateOutlet="copyrightInfo"></ng-container>
                        </div>
                    </div>

                    <div class="d-block text-center pb-5">
                        ({{ 'If you do not receive a message after a few minutes, please check your spam folder.' }})
                    </div>
                </ng-template>

                <ng-template #searchOrganizationsStep>
                    <div class="card">
                        <div class="card-body">
                            <h2>{{ 'Search for your association' }}</h2>

                            <div #hoaResults class="mat-form-search">
                                <lf-material-search #organizationSearch
                                                    defaultPlaceholder="{{'Find your HOA'}}"
                                                    [hasGroupAddon]="false"
                                                    (updated)="onSearchOrganizations($event)"
                                                    (click)="openIfResults()"></lf-material-search>
                                <div *ngIf="matMenuOpened && searchOrganizationsString.length >= 5"
                                     class="mat-menu-container animated-short fadeIn">
                                    <div class="mat-menu-panel">
                                        <ng-container *ngIf="isSearchingOrganizations; else results">
                                            <app-spinner-button>{{ 'Loading' }}</app-spinner-button>
                                        </ng-container>
                                        <ng-template #results>
                                            <ul class="list-group list-group-flush">
                                                <li *ngIf="orgResults.length === 0"
                                                    class="list-group-item">
                                                    <span class="d-flex px-3 py-2 text-muted">{{ 'No Results' }}</span>
                                                </li>
                                                <li *ngFor="let org of orgResults"
                                                    class="list-group-item">
                                                    <button class="btn text-left w-100"
                                                            mat-menu-item
                                                            (click)="onOrganizationSelected(org)">{{ org.name }}
                                                    </button>
                                                </li>
                                            </ul>
                                        </ng-template>
                                    </div>
                                </div>
                            </div>

                            <p class="notification-bar mt-3">
                                {{ 'Not finding your community? Please contact your Association Manager or Board of Directors.' }}
                                <span class="description-text pt-1">
                                    {{ 'Your association must have a PayHOA subscription in order to log in or make online payments.' }}
                                </span>
                            </p>
                        </div>
                    </div>
                </ng-template>

                <ng-template #signupFormDetails>
                    <div class="card">
                        <!-- px-4 px-md-5 pt-5 pb-4 -->
                        <div class="card-body with-footer">
                            <h2>{{ 'Please fill in your account details' }}</h2>

                            <ng-container *ngIf="signupForm.get('role').value === signupFormTypes.BOARD_MEMBER">
                                <ng-container *ngTemplateOutlet="groupNameFormControl; context:{ label:'Community name', placeholder:'Your local HOA\'s name' }"></ng-container>
                                <ng-container *ngTemplateOutlet="emailFormControl"></ng-container>
                            </ng-container>

                            <ng-container *ngIf="signupForm.get('role').value === signupFormTypes.PROPERTY_MANAGER">
                                <ng-container *ngTemplateOutlet="groupNameFormControl; context:{ label:'Business name', placeholder:'Your property management company' }"></ng-container>
                                <ng-container *ngTemplateOutlet="emailFormControl"></ng-container>
                            </ng-container>


                            <div class="form-group">
                                <label class="text-left font-weight-bold form-control-required" for="full-name">{{ 'Full name' }}</label>
                                <input #fullNameInput
                                       id="full-name"
                                       class="form-control"
                                       formControlName="fullName"
                                       autocomplete="name"
                                       placeholder="{{'Your name'}}"
                                       required
                                       type="text"/>
                                <app-field-error-display [control]="isFieldValid('fullName')"></app-field-error-display>
                            </div>

                            <ng-container *ngIf="signupForm.get('role').value !== signupFormTypes.HOMEOWNER; else addressFormGroup">
                                <div class="form-group">
                                    <label class="text-left font-weight-bold form-control-required" for="full-name">{{ 'Phone number' }}</label>
                                    <input id="phone"
                                           class="form-control"
                                           formControlName="phone"
                                           autocomplete="tel-national"
                                           placeholder="{{'555-555-5555'}}"
                                           required
                                           type="tel"
                                           [mask]="phoneNumberValidationMask"/>
                                    <app-field-error-display [control]="isFieldValid('phone')"></app-field-error-display>
                                </div>

                                <div class="form-group p-relative">
                                    <div *ngIf="passwordHasFocus"
                                         class="mat-menu-container password-hinter animated fadeInBottom">
                                        <div class="triangle-with-shadow"></div>
                                        <ul class="mat-menu-panel list-group list-group-flush">
                                            <li class="list-group-item list-group-header">{{ 'Password must include the following:' }}</li>
                                            <li *ngFor="let validation of passwordValidations"
                                                class="list-group-item">
                                                <i [ngClass]="validation.icon"></i>{{ validation.text }}
                                            </li>
                                        </ul>
                                    </div>

                                    <label class="text-left font-weight-bold form-control-required" for="password">{{ 'Password' }}</label>
                                    <mat-form-field appearance="outline">
                                        <input id="password"
                                               formControlName="password"
                                               autocomplete="new-password"
                                               minlength="8"
                                               matInput
                                               required
                                               validatePassword
                                               stacked="true"
                                               [placeholder]="passwordHide ? '&bull;&bull;&bull;&bull;&bull;&bull;&bull;' : 'Your password'"
                                               [type]="passwordHide ? 'password' : 'text'"
                                               (keyup)="updateValidation('password')"
                                               (focus)="passwordHasFocus = true"
                                               (blur)="passwordHasFocus = false"/>
                                        <button id="password-visibility"
                                                matSuffix
                                                type="button"
                                                [attr.aria-label]="'Hide password'"
                                                [attr.aria-pressed]="passwordHide"
                                                (click)="passwordHide = !passwordHide">
                                            <mat-icon>{{ passwordHide ? 'visibility_off' : 'visibility' }}</mat-icon>
                                        </button>
                                    </mat-form-field>
                                    <app-field-error-display [useConsolidatedMessage]="true"
                                                             [control]="isFieldValid('password')"></app-field-error-display>
                                </div>
                            </ng-container>

                            <div *ngIf="selectedStep === signupSteps.ADD_SIGNUP_DETAILS && environment.production"
                                 class="captcha-container form-group pt-3 animated fadeIn"
                                 [class.ng-invalid]="signupForm.get('captcha').invalid && signupForm.get('captcha').touched">
                                <div>
                                    <re-captcha #captcha
                                                formControlName="captcha"
                                                ngDefaultControl
                                                siteKey="{{siteKey}}"
                                                (resolved)="signupForm.get('captcha').setValue($event)"></re-captcha>
                                </div>
                                <app-field-error-display [control]="isFieldValid('captcha')"></app-field-error-display>
                            </div>
                        </div>

                        <div class="card-footer d-flex flex-column justify-content-center">
                            <app-spinner-button *ngIf="isSubmitting"
                                                [buttonClasses]="['mx-auto']">{{ signupForm.get('role').value === signupFormTypes.HOMEOWNER ? 'Sending Request' : 'Creating Account' }}
                            </app-spinner-button>
                            <app-button *ngIf="!isSubmitting"
                                        type="primary"
                                        [buttonClasses]="['mr-0']"
                                        [isSubmit]="true">{{ signupForm.get('role').value === signupFormTypes.HOMEOWNER ? 'Request to Join HOA' : 'Create an Account' }}
                            </app-button>
                            <ng-container *ngTemplateOutlet="copyrightInfo"></ng-container>
                        </div>
                    </div>
                </ng-template>

                <ng-template #joinComplete>
                    <div class="card">
                        <div class="card-body with-footer">
                            <h2>{{ 'Request is on its way' }}</h2>

                            <p class="notification-bar mt-3">
                                {{ 'We have emailed ' + signupForm.get('groupName').value + ' to review your request.' }}
                                <span class="description-text pt-1">
                                    {{ 'Once approved, a registration email will be sent to ' + signupForm.get('email').value + ' to complete your account registration.' }}
                                </span>
                            </p>
                        </div>

                        <div class="card-footer d-flex flex-column justify-content-center">
                            <app-button type="primary"
                                        [buttonClasses]="['mr-0']"
                                        [routeTo]="[loginRoute]">{{ 'Back to Login' }}
                            </app-button>
                            <ng-container *ngTemplateOutlet="copyrightInfo"></ng-container>
                        </div>
                    </div>

                    <div class="d-block text-center pb-5">
                        ({{ 'If you do not receive a message from your HOA, please check your spam folder.' }})
                    </div>
                </ng-template>
            </form>
        </div>
    </div>
</ng-container>

<ng-template #signupContainer let-step="step">
    <div class="d-flex align-items-md-center justify-content-center min-vh-100 animated fadeIn"
         [class.hidden]="!signupSteps[selectedStep]">
        <div class="row flex-column w-100">
            <ng-template #payhoaLogo>
                <a href="{{payHoaLink}}">
                    <img class="logo img mx-auto mx-lg-0"
                         alt="{{'Home'}}"
                         src="{{payHoaLogo}}"/>
                </a>
            </ng-template>
            <div class="d-block mx-auto text-center">
                <ng-container *ngIf="brandedSignup; else payhoaLogo">
                    <img class="logo branded img mx-auto mx-lg-0"
                         alt="{{'Home'}}"
                         src="{{signupOrganization.logoSrc}}"/>
                </ng-container>
            </div>

            <ng-container *ngIf="selectedStep === signupSteps.SELECT_SIGNUP_TYPE">
                <h1 class="text-center mb-0">{{ 'Get started with PayHOA' }}</h1>
                <h2 class="text-center">{{ 'Select the option that best describes you:' }}</h2>
            </ng-container>
            <ng-container *ngIf="brandedSignup">
                <h1 class="text-center mb-4">{{ 'Welcome to ' + signupOrganization.name }}</h1>
            </ng-container>

            <div *ngIf="step">
                <ng-container [ngTemplateOutlet]="step"></ng-container>

                <ng-container *ngIf="selectedStep === signupSteps.SELECT_SIGNUP_TYPE">
                    <ng-container *ngTemplateOutlet="copyrightInfo; context: {isFooter: true}"></ng-container>
                </ng-container>

                <div *ngIf="brandedSignup"
                     class="d-flex align-items-center text-center justify-content-center pt-4">
                    <span class="font-size-normal d-inline-flex mt-2">{{ 'Powered by ' }}</span>
                    <a class="d-inline-flex"
                       href="{{payHoaLink}}">
                        <img class="powered-by img"
                             alt="{{'PayHOA'}}"
                             src="{{payHoaLogo}}"/>
                    </a>
                </div>
            </div>
        </div>
    </div>
</ng-template>

<ng-template #copyrightInfo let-isFooter="isFooter">
    <div [class.text-center]="isFooter"
         class="pt-4">
        <div class="w-100"
             [class.mt-5]="isFooter"
             [class.description-text]="!isFooter">
            <div class="font-size-normal d-inline-block text-nowrap">
                <span>{{ isFooter ? '© ' + year + ' PayHOA, LLC. All rights reserved.' : 'By signing up, you agree to the' }}</span>
            </div>
            <div class="d-inline-block text-nowrap">
                <app-button type="link"
                            [buttonClasses]="['mr-0']"
                            [isInline]="true"
                            (onClick)="openTermsOfService()">{{ 'Terms of Service' }}
                </app-button>
                <span>{{ isFooter ? '|' : 'and' }}</span>
                <app-button type="link"
                            [buttonClasses]="['mr-0']"
                            [isInline]="true"
                            (onClick)="openPrivacyPolicy()">{{ 'Privacy Policy' }}
                </app-button>
            </div>
        </div>
    </div>
</ng-template>
